import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImSpinner8 } from 'react-icons/im';

export interface GooglePayParamsProps {
    token: string | null;
    billingAddress: {
        name: string;
        address1: string;
        address2?: string;
        locality: string;
        administrativeArea: string;
        postalCode: string;
        countryCode: string;
    } | null;
    email: string | null;
}

interface GooglePayButtonProps {
    valueTotal: number;
    method: any;
    onCreateOrder: ({ token, billingAddress, email }: GooglePayParamsProps) => void;
    onCancel: CallableFunction;
    loading: boolean;
}

const GooglePayButton: React.FC<GooglePayButtonProps> = ({
    valueTotal,
    method,
    onCreateOrder,
    onCancel = () => {},
    loading = true
    }) => {

        const { t } = useTranslation();
        const containerRef = useRef<HTMLDivElement>(null);
        const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
        const [isButtonReady, setIsButtonReady] = useState(false);
        const [paymentsClient, setPaymentsClient] = useState<any>(null);

        const paymentRequest = useMemo(() => ({
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['VISA', 'MASTERCARD'],
                        billingAddressRequired: true,
                        billingAddressParameters: {
                            format: 'FULL',
                        },
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: method.gateway_merchant_name,
                            gatewayMerchantId: method.gateway_merchant_id,
                        },
                    },
                },
            ],
            emailRequired: true,
            merchantInfo: {
                merchantName: method.google_pay_merchant_name,
                merchantId: method.google_pay_merchant_id,
            },
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPriceLabel: 'Total',
                totalPrice: valueTotal ? valueTotal.toFixed(2) : '0.00',
                currencyCode: 'BRL',
                countryCode: 'BR',
            },
        }), [method, valueTotal]);

    // Etapa 1: Verifica se está disponível
    useEffect(() => {
        if (!method || valueTotal == null) return;

        if (
            typeof window === 'undefined' ||
            !window.google ||
            !window.google.payments ||
            !window.google.payments.api
        ) {
            setIsAvailable(false);
            return;
        }

        const client = new window.google.payments.api.PaymentsClient({
            environment: 'PRODUCTION',
        });

        setPaymentsClient(client);

        client
            .isReadyToPay(paymentRequest)
            .then((res: { result: boolean }) => {
                setIsAvailable(Boolean(res.result));
            })
            .catch(() => {
                setIsAvailable(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [method, valueTotal]);

    // Etapa 2: Cria o botão quando possível
    useEffect(() => {
        if (isAvailable && paymentsClient && containerRef.current) {
            const button = paymentsClient.createButton({
                buttonColor: 'default',
                buttonType: 'buy', // 'buy' | short'
                buttonSizeMode: 'fill',
                buttonLocale: 'pt',
                onClick: async () => {
                    try {
                        const paymentData = await paymentsClient.loadPaymentData(paymentRequest);
                        const token = paymentData?.paymentMethodData?.tokenizationData?.token;
                        const billingAddress = paymentData?.paymentMethodData?.info?.billingAddress;
                        const email = paymentData?.email;
                        onCreateOrder({ token, billingAddress, email });
                    } catch (err) {
                        onCancel();
                    }
                },
            });

            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(button);
            setIsButtonReady(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAvailable, paymentsClient]);

    return (
        <div className="w-full h-[46px] relative rounded-full overflow-hidden">
            <div ref={containerRef} id="containerButton" className="w-full h-full rounded-full overflow-hidden" />

            {loading || isAvailable === null || (isAvailable && !isButtonReady) ? (
                <div className="absolute inset-0 flex items-center justify-center gap-1 bg-neutral-900 text-neutral-300 rounded">
                    <ImSpinner8 className="animate-spin" />
                    <span>{t('checkout.google_pay.loading')}</span>
                </div>
            ) : null}

            {isAvailable === false && (
                <div className="absolute inset-0 flex items-center justify-center gap-1 bg-gray-100 text-gray-400 italic text-sm rounded">
                    {t('checkout.google_pay.unavailable')}
                </div>
            )}
        </div>
    );
};

export default GooglePayButton;
